import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  Table,
  Button,
  Badge,
  Spinner,
} from "reactstrap";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import axiosInstance from "../../../helpers/axiosInstance";
import gsap from "gsap";
import { toast } from "react-toastify";

const EcommerceProducts = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const observerRef = useRef(null);
  const [selectedProducts, setSelectedProducts] = useState([]); // State to store selected product IDs
  const [showBulkDelete, setShowBulkDelete] = useState(false); // State to control Bulk Delete button visibility
  const [searchTerm, setSearchTerm] = useState("");
  const LIMIT = 50;
  const isFetchingRef = useRef(false);
  const fetchedPagesRef = useRef(new Set());
  const [breadcrumbItems] = useState([
    { title: "Ecommerce", link: "#" },
    { title: "Products", link: "#" },
  ]);

  useEffect(() => {
    fetchProducts(1, searchTerm);
    fetchCategories();
  }, []);

  const fetchProducts = async (page = 1, name = "") => {
    const requestKey = `${name.trim()}-${page}`;

    // Prevent duplicate same-page calls
    if (isFetchingRef.current) return;
    if (fetchedPagesRef.current.has(requestKey)) return;

    try {
      isFetchingRef.current = true;
      fetchedPagesRef.current.add(requestKey);
      setLoading(true);

      const response = await axiosInstance.get(
        `api/v1/products?page=${page}&limit=${LIMIT}${name ? `&name=${encodeURIComponent(name)}` : ""}`
      );

      const newProducts = response.data.products || [];

      if (page === 1) {
        setProducts(newProducts);
      } else {
        // Extra safety: dedupe by _id
        setProducts((prev) => {
          const existingIds = new Set(prev.map((p) => p._id));
          const uniqueNewProducts = newProducts.filter(
            (p) => !existingIds.has(p._id)
          );

          return [...prev, ...uniqueNewProducts];
        });
      }

      setTotalProducts(response.data.total || 0);
      setCurrentPage(page);
    } catch (error) {
      fetchedPagesRef.current.delete(requestKey);

      console.error("Error fetching products:", error);
      toast.error("Failed to load products. Please refresh the page.");
    } finally {
      isFetchingRef.current = false;
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axiosInstance.get("api/v1/categories");
      const categoryMap = {};
      response.data.categories.forEach((cat) => {
        categoryMap[cat._id] = cat.name;
      });
      setCategories(categoryMap);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const handleObserver = useCallback(
    (entries) => {
      const target = entries[0];

      if (!target.isIntersecting) return;
      if (isFetchingRef.current) return;
      if (products.length >= totalProducts) return;

      fetchProducts(currentPage + 1, searchTerm);
    },
    [products.length, totalProducts, currentPage, searchTerm]
  );

  const handleDelete = async (productId) => {
    if (window.confirm("Are you sure you want to delete this product?")) {
      try {
        setLoading(true);
        await axiosInstance.delete(`api/v1/admin/product/${productId}`);
        toast.success("Product deleted successfully");
        setProducts((prevProducts) => prevProducts.filter(product => product._id !== productId));
      } catch (error) {
        console.error("Error deleting product:", error);
        alert(error.response?.data?.message || "Error deleting product");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleBulkDelete = async () => {
    if (selectedProducts.length === 0) {
      alert("Please select at least one product to delete.");
      return;
    }

    if (window.confirm("Are you sure you want to delete the selected products?")) {
      try {
        setLoading(true);
        await axiosInstance.post("api/v1/admin/products/delete", {
          productIds: selectedProducts,
        });
        setProducts((prevProducts) =>
          prevProducts.filter(product => !selectedProducts.includes(product._id))
        );
        toast.success("Product deleted successfully");
        setSelectedProducts([]);
        setShowBulkDelete(false);

      } catch (error) {
        console.error("Error deleting products:", error);
        alert(error.response?.data?.message || "Error deleting products");
      } finally {
        setLoading(false);
      }
    }
  };


  useEffect(() => {
    const node = observerRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(handleObserver, {
      root: null,
      rootMargin: "200px",
      threshold: 0,
    });

    observer.observe(node);

    return () => observer.disconnect();
  }, [handleObserver]);

  const handleCheckboxChange = (productId) => {
    let updatedSelectedProducts;
    if (selectedProducts.includes(productId)) {
      updatedSelectedProducts = selectedProducts.filter(
        (id) => id !== productId
      );
    } else {
      updatedSelectedProducts = [...selectedProducts, productId];
    }
    setSelectedProducts(updatedSelectedProducts);
    setShowBulkDelete(updatedSelectedProducts.length > 0); // Show Bulk Delete if at least one product is selected
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(price);
  };

  const getDiscountedPrice = (price, discount) => {
    const discountedPrice = price - (price * discount) / 100;
    return formatPrice(discountedPrice);
  };


  const handleSearchSubmit = () => {
    fetchedPagesRef.current.clear();
    isFetchingRef.current = false;

    setProducts([]);
    setCurrentPage(1);
    setTotalProducts(0);

    fetchProducts(1, searchTerm.trim());
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="Products" breadcrumbItems={breadcrumbItems} />

        <Row>
          <Col lg={12}>
            <Card>
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <CardTitle className="h4">Products List</CardTitle>
                  <div className="d-flex gap-2">
                    <div className="d-flex gap-2" style={{ minWidth: "240px" }}>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search products..."
                        value={searchTerm}
                        onChange={handleSearch}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            handleSearchSubmit();
                          }
                        }}
                      />
                      <Button 
                        color="primary" 
                        onClick={handleSearchSubmit}
                      >
                        Search
                      </Button>
                      {searchTerm && (
                        <Button
                          color="secondary"
                          onClick={() => {
                            fetchedPagesRef.current.clear();
                            isFetchingRef.current = false;

                            setSearchTerm("");
                            setProducts([]);
                            setCurrentPage(1);
                            setTotalProducts(0);

                            fetchProducts(1, "");
                          }}
                        >
                          Clear
                        </Button>
                      )}
                    </div>
                    {showBulkDelete && (
                      <Button
                        color="danger"
                        onClick={handleBulkDelete}
                        className="me-2"
                      >
                        Bulk Delete
                      </Button>
                    )}
                    <Link to="/ecommerce-add-product">
                      <Button color="primary">Add New Product</Button>
                    </Link>
                  </div>
                </div>


                <div className="table-responsive">
                  <Table className="table-centered table-nowrap mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>
                          <input
                            type="checkbox"
                            checked={
                              products.length > 0 && selectedProducts.length === products.length
                            }
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedProducts(
                                  products.map((p) => p._id)
                                );
                                setShowBulkDelete(true);
                              } else {
                                setSelectedProducts([]);
                                setShowBulkDelete(false);
                              }
                            }}
                          />
                        </th>
                        <th>Product</th>
                        <th>Category</th>
                        <th>Price</th>
                        <th>Stock</th>
                        <th>Age Group</th>
                        <th>Weight</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {products.map((product, index) => (
                        <tr
                          key={product._id}
                          className="product-row"
                          style={{
                            opacity: 0,
                            transform: "translateY(20px)",
                          }}
                          ref={(el) => {
                            if (el) {
                              gsap.to(el, {
                                opacity: 1,
                                y: 0,
                                duration: 0.1,
                                delay: index * 0.01,
                              });
                            }
                          }}
                        >
                          <td>
                            <input
                              type="checkbox"
                              checked={selectedProducts.includes(product._id)}
                              onChange={() =>
                                handleCheckboxChange(product._id)
                              }
                            />
                          </td>
                          <td className="max-w-[200px]">
                            <div className="d-flex align-items-center text-center justify-center max-w-[200px]">
                              <img
                                src={product.images[0].startsWith("https") ? product.images[0] : `${process.env.REACT_APP_BASEURL}${product.images[0]}`}
                                alt={product.name}
                                className="rounded me-3"
                                height="64"
                                width="60"
                              />
                              <div>
                              <h5 style={{
                                    textOverflow:"ellipsis",
                                    overflow:"hidden",
                                    maxWidth:"200px"
                                  }} className="font-size-14 mb-1">
                                  <Link
                                  
                                    to={`/ecommerce-product-detail/${product._id}`}
                                    className="text-dark max-w-[200px]  overflow-hidden"
                                    target="_blank"
                                  >
                                    {product.name}
                                  </Link>
                                </h5>
                                <p className="text-muted mb-0">
                                  <Badge
                                    color="info"
                                    className="me-1 px-1 py-1"
                                  >
                                    {product?.brand?.title || "No Brand"}
                                  </Badge>
                                </p>
                              </div>
                            </div>
                          </td>
                          <td>{product?.category?.name || "No Category"}</td>
                          <td>
                            <div>
                              <h5 className="font-size-14 mb-1">
                                {getDiscountedPrice(
                                  product.price,
                                  product.discount
                                )}
                              </h5>
                              {product.discount > 0 && (
                                <div>
                                  <del className="text-muted me-1">
                                    {formatPrice(product.price)}
                                  </del>
                                  <Badge color="success">
                                    {product.discount}% off
                                  </Badge>
                                </div>
                              )}
                            </div>
                          </td>
                          <td>
                            <Badge
                              color={product.stock > 0 ? "success" : "danger"}
                              className="px-2"
                            >
                              {product.stock > 0
                                ? `In Stock (${product.stock})`
                                : "Out of Stock"}
                            </Badge>
                          </td>
                          <td>{product.ageGroup}</td>
                          <td style={!product.volumetricWeight ? {backgroundColor:"red"}:{}}>{product.volumetricWeight || ""} KG</td>
                          <td>
                            <Link
                              to={`/ecommerce-product-detail/${product._id}`}
                              className="me-2"
                              target="_blank"
                            >
                              <Button color="info" size="sm">
                                View
                              </Button>
                            </Link>
                            <Button
                              color="danger"
                              size="sm"
                              onClick={() => handleDelete(product._id)}
                            >
                              Delete
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>

                  {products.length === 0 && !loading && (
                    <div className="text-center my-5">
                      <h4>No products found</h4>
                      <Link to="/ecommerce-add-product">
                        <Button color="primary" className="mt-2">
                          Add Your First Product
                        </Button>
                      </Link>
                    </div>
                  )}
                </div>


                {/* Conditionally render pagination */}
                {/* {!showBulkDelete && (
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '20px' }}>
                    <div className="flex justify-center my-4 align-items-center gap-2">
                      <button
                        className={`bg-primary text-white font-semibold py-2 px-4 rounded-l ${currentPage === 1 ? 'opacity-50 cursor-not-allowed' : ''}`}
                        disabled={currentPage === 1}
                        onClick={() => handlePageChange(currentPage - 1)}
                      >
                        Previous
                      </button>
                      <span className="flex items-center mx-4 text-lg">Page {currentPage}</span>
                      <button
                        className={`bg-primary text-white font-semibold py-2 px-4 rounded-r ${currentPage * 10 >= totalProducts ? 'opacity-50 cursor-not-allowed' : ''}`}
                        disabled={currentPage * 10 >= totalProducts}
                        onClick={() => handlePageChange(currentPage + 1)}
                      >
                        Next
                      </button>
                    </div>
                  
                  </div>
                )} */}


              </CardBody>
            </Card>
          </Col>
        </Row>

        {loading && (
          <div className="text-center my-4">
            <Spinner color="primary" />
          </div>
        )}
        <div
          ref={observerRef}
          style={{ height: "10px", marginBottom: "20px" }}
        ></div>
      </Container>

    </div>
  );
};

export default EcommerceProducts;
